import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HiX } from 'react-icons/hi';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../utils/errorHandler';
import { formatDate } from '../utils/dateFormatter';
import { formatPKR } from '../utils/currencyFormatter';
import { API } from '../config';
import axios from 'axios';
import './BranchDetails.css';

const BranchDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPurchasesModal, setShowPurchasesModal] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  const getLocalDateStr = (d) => {
    const date = d instanceof Date ? d : new Date(d);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };
  const todayLocal = getLocalDateStr(new Date());
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  const [purchaseFilters, setPurchaseFilters] = useState({
    fromDate: getLocalDateStr(oneMonthAgo),
    toDate: todayLocal
  });
  const purchaseFiltersRef = useRef(purchaseFilters);
  useEffect(() => { purchaseFiltersRef.current = purchaseFilters; }, [purchaseFilters]);

  useEffect(() => {
    fetchBranchDetails();
  }, [id]);

  const fetchBranchDetails = async () => {
    try {
      const response = await axios.get(`${API}/branches/${id}`);
      setBranch(response.data.branch);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchases = async () => {
    if (!branch) return;
    const businessId = branch.business_id ?? branch.business?.id ?? user?.business_id;
    if (!businessId) return;
    const currentFilters = purchaseFiltersRef.current || purchaseFilters;
    setPurchasesLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('branchId', branch.id);
      if (currentFilters.fromDate) params.set('fromDate', currentFilters.fromDate);
      if (currentFilters.toDate) params.set('toDate', currentFilters.toDate);
      const res = await axios.get(`${API}/purchases/business/${businessId}?${params}`);
      setPurchases(res.data.purchases || []);
    } catch (err) {
      setPurchases([]);
    } finally {
      setPurchasesLoading(false);
    }
  };

  useEffect(() => {
    if (showPurchasesModal && branch) fetchPurchases();
  }, [showPurchasesModal, branch?.id]);

  if (loading) {
    return (
      <Layout>
        <div className="branch-details-page">
          <Loader message="Loading branch details..." variant="page" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="branch-details-page">
          <div className="error-banner">{error}</div>
          <button onClick={() => navigate('/branches')} className="back-button">
            ← Back to Branches
          </button>
        </div>
      </Layout>
    );
  }

  if (!branch) {
    return (
      <Layout>
        <div className="branch-details-page">
          <div className="error-banner">Branch not found</div>
          <button onClick={() => navigate('/branches')} className="back-button">
            ← Back to Branches
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="branch-details-page">
        <div className="page-header">
          <div>
            <button onClick={() => navigate('/branches')} className="back-button">
              ← Back to Branches
            </button>
            <h1>Branch Details</h1>
            <p className="page-subtitle">View complete information about this branch</p>
          </div>
        </div>

        <div className="details-container">
          <div className="details-card">
            <div className="card-header">
              <h2>{branch.name}</h2>
              <span className={`status-badge ${branch.is_active ? 'active' : 'inactive'}`}>
                {branch.is_active ? 'ACTIVE' : 'INACTIVE'}
              </span>
            </div>

            <div className="details-section">
              <h3>Basic Information</h3>
              <div className="details-grid">
                <div className="detail-item">
                  <label>Branch Code</label>
                  <p className="code-cell">BR-{String(branch.id).padStart(3, '0')}</p>
                </div>
                <div className="detail-item">
                  <label>Name</label>
                  <p>{branch.name}</p>
                </div>
                <div className="detail-item">
                  <label>Business</label>
                  <p>{branch.business?.name || '—'}</p>
                </div>
                <div className="detail-item">
                  <label>Email</label>
                  <p>{branch.email || '—'}</p>
                </div>
                <div className="detail-item">
                  <label>Phone</label>
                  <p>{branch.phone || '—'}</p>
                </div>
                <div className="detail-item full-width">
                  <label>Address</label>
                  <p>{branch.address || '—'}</p>
                </div>
                <div className="detail-item full-width">
                  <label>Description</label>
                  <p>{branch.description || '—'}</p>
                </div>
                <div className="detail-item">
                  <label>Created At</label>
                  <p>{formatDate(branch.created_at)}</p>
                </div>
              </div>
            </div>

            <div className="details-section">
              <h3>Statistics</h3>
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-value">{branch.users?.length || 0}</div>
                  <div className="stat-label">Total Users</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{branch.users?.filter(u => u.role === 'manager').length || 0}</div>
                  <div className="stat-label">Managers</div>
                </div>
              </div>
            </div>

            {/* Managers Section */}
            <div className="details-section">
              <h3>Branch Managers</h3>
              {branch.users && branch.users.filter(u => u.role === 'manager').length > 0 ? (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Username</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Status</th>
                        <th>Created At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {branch.users.filter(u => u.role === 'manager').map((manager) => (
                        <tr key={manager.id} className="manager-row">
                          <td className="font-semibold">{manager.username}</td>
                          <td>{manager.name || '—'}</td>
                          <td>{manager.email || '—'}</td>
                          <td>{manager.phone || '—'}</td>
                          <td>
                            <span className={`status-badge ${manager.is_active ? 'active' : 'inactive'}`}>
                              {manager.is_active ? 'ACTIVE' : 'INACTIVE'}
                            </span>
                          </td>
                          <td className="created-at-cell">{formatDate(manager.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <p>No managers assigned to this branch yet.</p>
                </div>
              )}
            </div>

            {/* All Users Section */}
            {branch.users && branch.users.length > 0 && (
              <div className="details-section">
                <h3>All Users ({branch.users.length})</h3>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Username</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Created At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {branch.users.map((userItem) => (
                        <tr key={userItem.id} className={userItem.role === 'manager' ? 'manager-row' : ''}>
                          <td className="font-semibold">{userItem.username}</td>
                          <td>{userItem.name || '—'}</td>
                          <td>{userItem.email || '—'}</td>
                          <td>{userItem.phone || '—'}</td>
                          <td>
                            <span className="badge">{userItem.role?.replace('_', ' ').toUpperCase()}</span>
                          </td>
                          <td>
                            <span className={`status-badge ${userItem.is_active ? 'active' : 'inactive'}`}>
                              {userItem.is_active ? 'ACTIVE' : 'INACTIVE'}
                            </span>
                          </td>
                          <td className="created-at-cell">{formatDate(userItem.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Branch Analytics */}
            <div className="details-section analytics-placeholder">
              <h3>Branch Analytics</h3>
              <div className="analytics-grid">
                <button
                  type="button"
                  className="analytics-card analytics-card-clickable"
                  onClick={() => navigate('/sales')}
                >
                  <div className="analytics-icon">📊</div>
                  <h4>Sales</h4>
                  <p className="analytics-note">View sales and create new sales</p>
                </button>
                <button
                  type="button"
                  className="analytics-card analytics-card-clickable"
                  onClick={() => setShowPurchasesModal(true)}
                >
                  <div className="analytics-icon">🛒</div>
                  <h4>Purchases</h4>
                  <p className="analytics-note">View purchases (filter by date)</p>
                </button>
                <button
                  type="button"
                  className="analytics-card analytics-card-clickable"
                  onClick={() => navigate('/products')}
                >
                  <div className="analytics-icon">📦</div>
                  <h4>Inventory</h4>
                  <p className="analytics-note">View products and inventory</p>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Purchases modal */}
        {showPurchasesModal && (
          <div className="modal-overlay" onClick={() => setShowPurchasesModal(false)}>
            <div className="modal-content branch-purchases-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Purchases – {branch?.name}</h3>
                <button type="button" className="modal-close" onClick={() => setShowPurchasesModal(false)}><HiX /></button>
              </div>
              <div className="branch-purchases-filters">
                <div className="filter-group">
                  <label>From date</label>
                  <input
                    type="date"
                    value={purchaseFilters.fromDate}
                    onChange={e => setPurchaseFilters(f => ({ ...f, fromDate: e.target.value }))}
                  />
                </div>
                <div className="filter-group">
                  <label>To date</label>
                  <input
                    type="date"
                    value={purchaseFilters.toDate}
                    onChange={e => setPurchaseFilters(f => ({ ...f, toDate: e.target.value }))}
                  />
                </div>
                <button type="button" className="btn-primary" onClick={fetchPurchases} disabled={purchasesLoading}>
                  {purchasesLoading ? 'Loading…' : 'Apply'}
                </button>
              </div>
              {purchasesLoading ? (
                <Loader message="Loading purchases…" variant="block" />
              ) : (
                <div className="branch-purchases-table-wrap">
                  {purchases.length === 0 ? (
                    <p className="branch-purchases-empty">No purchases in this date range.</p>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Product code(s)</th>
                          <th>Product name(s)</th>
                          <th>Vendor</th>
                          <th>Status</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {purchases.map(p => {
                          const codes = (p.items || [])
                            .map(i => i.product?.product_code ?? i.product?.productCode)
                            .filter(Boolean);
                          const names = (p.items || [])
                            .map(i => i.product?.name ?? i.product?.productName)
                            .filter(Boolean);
                          const productCodes = [...new Set(codes)].join(', ') || '—';
                          const productNames = [...new Set(names)].join(', ') || '—';
                          return (
                            <tr key={p.id}>
                              <td>{p.purchase_date || p.purchaseDate || '—'}</td>
                              <td>{productCodes}</td>
                              <td>{productNames}</td>
                              <td>{p.party?.name || '—'}</td>
                              <td>
                                <span className={`status-badge ${p.status === 'confirmed' ? 'active' : 'inactive'}`}>
                                  {p.status || '—'}
                                </span>
                              </td>
                              <td>{formatPKR(p.total_amount ?? p.totalAmount ?? 0)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowPurchasesModal(false)}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BranchDetails;
